import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { monthNames } from '../constants';
import { formatTransactionFee } from '../utils/transactionFees';

const ReportDetail = () => {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/reports/${id}`);
        setReport(res.data.data);
      } catch (err) {
        setReport(null);
      }
      setLoading(false);
    };
    fetchReport();
  }, [id]);

  if (loading) return <div className="p-8">Loading...</div>;
  if (!report) return <div className="p-8 text-red-600">Report not found.</div>;

  return (
    <div className="w-full mx-auto px-0 py-6">
      <button data-testid="report-detail-back" className="mb-4 text-blue-600 underline" onClick={() => navigate(-1)}>&larr; Back</button>
      <h2 className="text-2xl font-bold mb-2">Report Details</h2>
      <div className="mb-4 text-sm text-gray-700">
        <div><b>Type:</b> {report.type}</div>
        <div><b>Month:</b> {monthNames[report.month - 1]}</div>
        <div><b>Year:</b> {report.year}</div>
        {report.meta?.usd_rate && <div><b>USD Rate:</b> {report.meta.usd_rate}</div>}
        <div><b>Created At:</b> {new Date(report.created_at).toLocaleString()}</div>
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-2">Rows</h3>
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {report.type === 'salary_sheet' ? (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ID</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total Days</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Days Worked</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Salary (PKR)</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Calculated Salary (PKR)</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PKR Amount (Manual)</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">PKR to be paid in USD</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">USD Rate</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Gross USD Amount</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction Fee (USD)</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net USD Amount</th>
                    </>
                  ) : (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(report.rows) && report.rows.length > 0 ? (
                  report.rows.map((row) => {
                    const d = row.data;
                    return report.type === 'salary_sheet' ? (
                      <tr key={row.id} data-testid={`report-detail-row-${row.id}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">{d.name || ''}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{d.employee_id || ''}</td>
                        <td className="px-6 py-4 text-center">{d.total_days || ''}</td>
                        <td className="px-6 py-4 text-center">{d.days_worked || ''}</td>
                        <td className="px-6 py-4 text-right">{d.salary_pkr || 0}</td>
                        <td className="px-6 py-4 text-right">{d.calculated_salary_pkr !== undefined ? Number(d.calculated_salary_pkr).toFixed(2) : ''}</td>
                        <td className="px-6 py-4 text-center">{d.manual_pkr || 0}</td>
                        <td className="px-6 py-4 text-right">
                          {d.pkr_to_be_paid_in_usd !== undefined ? 
                            Number(d.pkr_to_be_paid_in_usd).toFixed(2) : 
                            (d.calculated_salary_pkr !== undefined ? (Number(d.calculated_salary_pkr) - (d.manual_pkr || 0)).toFixed(2) : '')
                          }
                        </td>
                        <td className="px-6 py-4 text-right">{report.meta?.usd_rate || 0}</td>
                        <td className="px-6 py-4 text-right">
                          {d.gross_usd_amount !== undefined ? 
                            Number(d.gross_usd_amount).toFixed(2) : 
                            (report.meta?.usd_rate && report.meta.usd_rate > 0 && d.calculated_salary_pkr !== undefined ? 
                              ((Number(d.calculated_salary_pkr) - (d.manual_pkr || 0)) / report.meta.usd_rate).toFixed(2) : '0.00')
                          }
                        </td>
                        <td className="px-6 py-4 text-right text-sm">
                          {d.transaction_fee_usd !== undefined ? 
                            formatTransactionFee(Number(d.transaction_fee_usd)) : 
                            'N/A'
                          }
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-primary-600">
                          {d.net_usd_amount !== undefined ? 
                            Number(d.net_usd_amount).toFixed(2) : 
                            'N/A'
                          }
                        </td>
                      </tr>
                    ) : (
                      <tr key={row.id} data-testid={`report-detail-row-${row.id}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">{JSON.stringify(row.data)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={report.type === 'salary_sheet' ? 12 : 1} className="px-6 py-4 text-center text-gray-400">
                      No data available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDetail; 